import SEO from '@/components/SEO';
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useQuoteDrawer } from "@/context/QuoteDrawerContext";
import { Link } from "wouter";
import { 
  Smartphone, Globe, Zap, Search, Palette, Code2, 
  LayoutDashboard, Users, Award, MessageSquare, ChevronDown, 
  BarChart, Settings, Terminal, Layers, Cpu, Server, 
  ShoppingBag, Database, CheckCircle, ArrowRight, Target, 
  Code, LayoutGrid, Sparkles, Shield, BarChart2, 
  TrendingUp, Rocket, PenTool, Figma, Smartphone as DeviceMobile,
  LayoutTemplate, Monitor, Cloud, GraduationCap, MapPin
} from "lucide-react";

// Import components
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";
import { FeatureCard } from "@/components/services/FeatureCard";
import { TechStack } from "@/components/services/TechStack";
import { ProcessTimeline, type ProcessStep } from "@/components/services/ProcessTimeline";
import { AnimatedFAQ } from "@/components/services/AnimatedFAQ";
import { RelatedServices } from "@/components/RelatedServices";
import WebDesignLocations from '@/components/WebDesignLocations';

// Brand Logo SVGs
const BrandLogos = {
  shopify: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M21.5 8.6c-1.4-.6-2.1-1.1-2.1-1.8 0-.9 1.2-1.5 1.2-3.2 0-2.2-1.8-2.6-2.4-2.6h-11c-.6 0-2.4.4-2.4 2.6 0 1.7 1.2 2.3 1.2 3.2 0 .7-.8 1.2-2.1 1.8-1.4.6-2.4 1.4-2.4 2.6 0 1.2 1 2 2.4 2.6 1.3.6 2.1 1.1 2.1 1.8 0 .9-1.2 1.5-1.2 3.2 0 2.2 1.8 2.6 2.4 2.6h11c.6 0 2.4-.4 2.4-2.6 0-1.7-1.2-2.3-1.2-3.2 0-.7.8-1.2 2.1-1.8 1.4-.6 2.4-1.4 2.4-2.6 0-1.2-1-2-2.4-2.6zm-14.5 8.4c-1.4 0-2.5-1.1-2.5-2.5s1.1-2.5 2.5-2.5c1.4 0 2.5 1.1 2.5 2.5s-1.1 2.5-2.5 2.5z"/>
    </svg>
  ),
  amazon: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M6.8 14.4c-.1-.1-.2-.1-.3 0l-1.3 1.3c-.1.1-.1.2 0 .3l.6.6c.1.1.2.1.3 0l1.3-1.3c.1-.1.1-.2 0-.3l-.6-.6zm6.5-6.5c-.1-.1-.2-.1-.3 0l-8 8c-.1.1-.1.2 0 .3l.6.6c.1.1.2.1.3 0l8-8c.1-.1.1-.2 0-.3l-.6-.6z"/>
      <path d="M19.5 10.5c.7-1.1 1.1-2.4 1.1-3.8 0-3.3-2.7-6-6-6s-6 2.7-6 6c0 1.4.4 2.7 1.1 3.8-2.4 1.2-4.1 3.7-4.1 6.6 0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2 0-2.9-1.7-5.4-4.1-6.6zm-8.5-5.3c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4-4-1.8-4-4z"/>
    </svg>
  ),
  microsoft: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M11.5 3v8.5H3V3h8.5zm0 18H3v-8.5h8.5V21zM21 3h-8.5v8.5H21V3zm0 13.5h-8.5V21h8.5v-4.5z"/>
    </svg>
  ),
  google: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12.5 10.9c-2.5 0-4.5 2-4.5 4.6s2 4.6 4.5 4.6c1.4 0 2.5-1.1 4.1-2.8h-4.1v-3.6h7.2c.1.5.1 1 .1 1.5 0 5.1-3.4 8.7-7.3 8.7-4.1 0-7.5-3.4-7.5-7.5s3.4-7.5 7.5-7.5c2.1 0 3.5.8 4.5 1.8l2.1-2.1c-1.7-1.6-4.1-2.6-6.6-2.6-5.5 0-10 4.5-10 10s4.5 10 10 10 10-4.5 10-10c0-.7-.1-1.4-.2-2.1h-9.8z"/>
    </svg>
  ),
  apple: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.7 8.5c-1.5 0-2.3-1.1-2.1-1.8 0-.9 1.2-1.5 1.2-3.2 0-2.2-1.8-2.6-2.4-2.6h-11c-.6 0-2.4.4-2.4 2.6 0 1.7 1.2 2.3 1.2 3.2 0 .7-.8 1.2-2.1 1.8-1.4.6-2.4 1.4-2.4 2.6 0 1.2 1 2 2.4 2.6 1.3.6 2.1 1.1 2.1 1.8 0 .9-1.2 1.5-1.2 3.2 0 2.2 1.8 2.6 2.4 2.6h11c.6 0 2.4-.4 2.4-2.6 0-1.7-1.2-2.3-1.2-3.2 0-.7.8-1.2 2.1-1.8 1.4-.6 2.4-1.4 2.4-2.6 0-1.2-1-2-2.4-2.6zm-14.5 8.4c-1.4 0-2.5-1.1-2.5-2.5s1.1-2.5 2.5-2.5c1.4 0 2.5 1.1 2.5 2.5s-1.1 2.5-2.5 2.5z"/>
    </svg>
  ),
  spotify: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.48.66.3 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.4-11.939-1.32-.48.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.56 18.72 12.84c.36.18.54.78.24 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.3c-.6.18-1.2-.18-1.38-.72-.18-.6.18-1.2.72-1.38 4.26-1.26 11.28-1.02 15.721 1.62.54.3.72 1.02.42 1.56-.3.3-.84.48-1.38.18z"/>
    </svg>
  ),
  netflix: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M5.4 0v24h13.2V0H5.4zm9.1 21.6H6.6V2.4h8.1v19.2z"/>
      <path d="M8.8 16.8h2.4V7.2H8.8v9.6z"/>
    </svg>
  )
};

const FAQ_ITEMS = [
  {
    question: "How long does it take to build a website?",
    answer: "The timeline varies based on the project scope. A simple website typically takes 4-8 weeks, while complex web applications can take 3-6 months. We'll provide a detailed timeline after understanding your requirements."
  },
  {
    question: "What's included in your web design process?",
    answer: "Our process includes discovery & strategy, UI/UX design, development, testing, and deployment. We also provide training and documentation to help you manage your site after launch."
  },
  {
    question: "Do you provide website hosting?",
    answer: "Yes, we offer reliable hosting solutions with 99.9% uptime, automatic backups, and 24/7 monitoring. We can also help you migrate existing sites to our hosting platform."
  },
  {
    question: "How do you ensure my website is mobile-friendly?",
    answer: "We use a mobile-first approach, designing for mobile devices first and then scaling up. All our websites are fully responsive and tested across various devices and screen sizes."
  },
  {
    question: "What about SEO and website maintenance?",
    answer: "We implement SEO best practices during development and offer ongoing maintenance packages that include updates, security monitoring, and performance optimization."
  },
  {
    question: "Why choose Al Ain-based web services?",
    answer: "We understand the unique needs of border businesses and educational institutions in Al Ain"
  }
];

const features = [
  {
    title: "Responsive Web Design",
    description: "Beautiful, mobile-first designs that work perfectly on all devices and screen sizes with a focus on user experience and accessibility.",
    icon: <DeviceMobile className="h-6 w-6 text-primary" />
  },
  {
    title: "E-commerce Solutions",
    description: "Complete online store setup with secure payment processing, inventory management, and order fulfillment integration.",
    icon: <ShoppingBag className="h-6 w-6 text-primary" />
  },
  {
    title: "Web Applications",
    description: "Custom web applications built with modern frameworks to solve complex business challenges and improve productivity.",
    icon: <LayoutDashboard className="h-6 w-6 text-primary" />
  },
  {
    title: "Performance Optimization",
    description: "Lightning-fast websites with optimized assets, code splitting, and server-side rendering for better user experience and SEO.",
    icon: <Zap className="h-6 w-6 text-primary" />
  },
  {
    title: "SEO & Analytics",
    description: "Search engine optimization and analytics integration to help your website rank higher and track performance.",
    icon: <BarChart2 className="h-6 w-6 text-primary" />
  },
  {
    title: "Ongoing Support",
    description: "Dedicated support and maintenance packages to keep your website secure, updated, and running smoothly.",
    icon: <Shield className="h-6 w-6 text-primary" />
  },
  {
    title: "Education Focus",
    description: "Specialized solutions for Al Ain's universities and research institutions",
    icon: <GraduationCap className="h-6 w-6 text-primary" />
  },
  {
    title: "Border Business Ready",
    description: "Websites catering to Al Ain's unique position near the Oman border",
    icon: <MapPin className="h-6 w-6 text-primary" />
  }
];

const techStack = [
  { name: "React & Next.js", level: 95, icon: <Code2 className="h-5 w-5" /> },
  { name: "Node.js & Express", level: 90, icon: <Terminal className="h-5 w-5" /> },
  { name: "TypeScript", level: 90, icon: <Code className="h-5 w-5" /> },
  { name: "MongoDB & PostgreSQL", level: 85, icon: <Database className="h-5 w-5" /> },
  { name: "GraphQL & REST", level: 88, icon: <Layers className="h-5 w-5" /> },
  { name: "Docker & Kubernetes", level: 80, icon: <Server className="h-5 w-5" /> }
];

const designStack = [
  { name: "Figma & Adobe XD", level: 90, icon: <Figma className="h-5 w-5" /> },
  { name: "UI/UX Design", level: 92, icon: <Palette className="h-5 w-5" /> },
  { name: "Responsive Design", level: 95, icon: <LayoutGrid className="h-5 w-5" /> },
  { name: "Motion Design", level: 85, icon: <Sparkles className="h-5 w-5" /> },
  { name: "Design Systems", level: 88, icon: <Layers className="h-5 w-5" /> },
  { name: "Prototyping", level: 90, icon: <CheckCircle className="h-5 w-5" /> }
];

const process: ProcessStep[] = [
  {
    step: "01",
    title: "Discovery & Planning",
    description: "We start by understanding your business goals, target audience, and project requirements to create a detailed project plan.",
    icon: <Search className="h-5 w-5" />,
    duration: "1-2 weeks"
  },
  {
    step: "02",
    title: "UI/UX Design",
    description: "Our designers create wireframes and high-fidelity mockups to visualize the user interface and experience.",
    icon: <Palette className="h-5 w-5" />,
    duration: "2-4 weeks"
  },
  {
    step: "03",
    title: "Development",
    description: "Our developers bring the designs to life using modern web technologies and best practices.",
    icon: <Code2 className="h-5 w-5" />,
    duration: "4-12 weeks"
  },
  {
    step: "04",
    title: "Testing & QA",
    description: "We thoroughly test the website across devices and browsers to ensure quality and performance.",
    icon: <CheckCircle className="h-5 w-5" />,
    duration: "1-2 weeks"
  },
  {
    step: "05",
    title: "Launch & Support",
    description: "We handle the deployment and provide ongoing support to ensure your website's success.",
    icon: <Rocket className="h-5 w-5" />,
    duration: "Ongoing"
  }
];

const processSteps: ProcessStep[] = [
  {
    step: "01",
    title: "Al Ain Consultation",
    description: "Understanding your unique needs in Al Ain's market",
    icon: <MessageSquare className="h-5 w-5" />,
    duration: "1-2 weeks"
  },
  {
    step: "02",
    title: "Garden City Design",
    description: "Creating designs that reflect Al Ain's character",
    icon: <Palette className="h-5 w-5" />,
    duration: "2-3 weeks"
  }
];

const testimonials = [
  {
    name: "Al Ain University",
    quote: "The website perfectly serves our academic community and international students",
    role: "Education Sector"
  },
  {
    name: "Border Trading Company",
    quote: "Our Oman-UAE cross-border business needed specialized web solutions",
    role: "Trade Sector"
  }
];

const brands = [
  {
    name: "Al Ain University",
    logo: "/logos/alain-university.png"
  },
  {
    name: "Al Ain Chamber of Commerce",
    logo: "/logos/chamber-commerce.png"
  },
  {
    name: "Border Trade Association",
    logo: "/logos/border-trade.png"
  }
];

const WebDesignDevelopmentAlAin = () => {
  const { openDrawer } = useQuoteDrawer();
  
  return (
    <>
      <SEO 
        title="Web Design in Al Ain | ZeeXSoft"
        description="Garden city web solutions for Al Ain's educational institutions and border businesses"
        keywords="web design al ain, al ain university website, oman border business"
      />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/images/dot-pattern.svg')] bg-[size:40px_40px] opacity-5"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <motion.span 
                  className="inline-block px-4 py-2 text-sm font-medium rounded-full bg-blue-600 text-white mb-6 shadow-lg"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  Web Design & Development
                </motion.span>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Oasis of <span className="text-blue-600">Digital Solutions</span> For Al Ain
                </h1>
                <p className="text-lg text-gray-600 mb-8 max-w-lg">
                  Websites that reflect Al Ain's unique character as UAE's garden city and education hub.
                </p>
                <div className="flex flex-wrap gap-4">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Button 
                      className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
                      onClick={openDrawer}
                    >
                      Get a Free Consultation
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                    <Link href="/portfolio">
                      <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-100">
                        View Our Work
                      </Button>
                    </Link>
                  </motion.div>
                </div>
              </motion.div>
            </div>
            
            <div className="lg:w-1/2">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                {/* Browser mockup */}
                <div className="relative bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                  {/* Browser chrome */}
                  <div className="bg-gray-100 p-2 flex items-center border-b border-gray-200">
                    <div className="flex space-x-2 mr-3">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="flex-1 bg-white rounded text-xs px-3 py-1 text-gray-600 truncate">
                      https://yourbusiness.com
                    </div>
                  </div>
                  
                  {/* Website mock content */}
                  <div className="p-4 bg-white">
                    {/* Hero section mock */}
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="h-6 w-40 bg-blue-600 rounded mb-3"></div>
                      <div className="h-4 w-64 bg-gray-300 rounded mb-2"></div>
                      <div className="h-3 w-56 bg-gray-300 rounded mb-4"></div>
                      <div className="h-8 w-32 bg-blue-600 rounded"></div>
                    </div>
                    
                    {/* Features grid mock */}
                    <div className="grid grid-cols-2 gap-3">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="h-6 w-6 bg-blue-100 rounded-lg mb-2 flex items-center justify-center">
                            <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                          </div>
                          <div className="h-4 w-24 bg-gray-300 rounded mb-1"></div>
                          <div className="h-3 w-20 bg-gray-300 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Floating badges */}
                <div className="absolute -bottom-5 -right-5 flex space-x-2">
                  {['React', 'Tailwind', 'Framer'].map((tech) => (
                    <span key={tech} className="px-2 py-1 text-xs font-medium rounded-full bg-white text-gray-700 border border-gray-200 shadow-sm">
                      {tech}
                    </span>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Brands Section */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <p className="text-center text-sm text-muted-foreground mb-8">TRUSTED BY INNOVATIVE COMPANIES WORLDWIDE</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-70 grayscale hover:grayscale-0 transition-all duration-300">
            {Object.entries(BrandLogos).map(([key, Logo]) => (
              <div key={key} className="h-8 w-auto">
                <Logo className="h-full w-auto" />
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Web Solutions
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We offer end-to-end web development services to help you establish a strong online presence.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard 
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="md:w-1/2">
                <h2 className="text-3xl font-bold mb-6">
                  Our Tech Stack
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
                    {' '}Expertise
                  </span>
                </h2>
                <p className="text-muted-foreground mb-8">
                  We leverage cutting-edge technologies to build fast, scalable, and secure web applications. Our team stays updated with the latest industry trends to deliver top-notch solutions.
                </p>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Code2 className="h-5 w-5 text-primary" />
                      <h3 className="font-medium">Development</h3>
                    </div>
                    <TechStack 
                      items={techStack} 
                      title="" 
                      description=""
                    />
                  </div>
                </div>
              </div>
              <div className="md:w-1/2">
                <div className="space-y-4">
                  <div className="mt-12 md:mt-0">
                    <div className="flex items-center gap-2 mb-2">
                      <PenTool className="h-5 w-5 text-primary" />
                      <h3 className="font-medium">Design</h3>
                    </div>
                    <TechStack 
                      items={designStack} 
                      title="" 
                      description=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <ProcessTimeline 
            steps={processSteps}
            title="Our Process"
            description="Our Al Ain-specific development process accounts for educational and border business needs"
          />
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Clients Say
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Hear from our satisfied clients in Al Ain's education and trade sectors.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <p className="text-lg text-gray-600 mb-4">{testimonial.quote}</p>
                <p className="text-sm text-gray-500">{testimonial.name}</p>
                <p className="text-xs text-gray-400">{testimonial.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <AnimatedFAQ 
            items={FAQ_ITEMS}
            title="Frequently Asked Questions"
            description="Find answers to common questions about our web design and development services"
          />
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <RelatedServices 
            currentService="Web Design & Development" 
            services={[
              {
                title: "Cloud Services",
                description: "Scalable, secure, and high-performance cloud solutions tailored to your business needs.",
                icon: <Cloud className="h-8 w-8" />
              },
              {
                title: "Mobile App Development",
                description: "Native and cross-platform mobile applications for iOS and Android.",
                icon: <Smartphone className="h-8 w-8" />
              },
              {
                title: "UI/UX Design",
                description: "User-centered design solutions that enhance user experience and engagement.",
                icon: <PenTool className="h-8 w-8" />
              }
            ]} 
          />
        </div>
      </section>

      {/* Web Design Locations */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <WebDesignLocations />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-accent/5 -z-10"></div>
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Transform Your
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
                {' '}Online Presence?
              </span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's build something amazing together. Get in touch with our team to discuss your project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-base font-medium group"
                onClick={openDrawer}
              >
                Get Started
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="text-base font-medium group"
                asChild
              >
                <Link href="/contact">
                  Contact Us
                  <MessageSquare className="ml-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default WebDesignDevelopmentAlAin;
